/**
 * PUDO Release - Complete delivery to customer
 * Scans RECIPIENT QR or enters OTP to mark as DELIVERED
 * This is the ONLY way to complete a delivery
 */
import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import api from '../lib/api';
import {
  ArrowLeft,
  QrCode,
  Camera,
  CheckCircle,
  AlertCircle,
  Package,
  Users,
  Scan,
  KeyRound,
  Smartphone
} from 'lucide-react';

const PudoRelease = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const preselectedPackageId = location.state?.packageId || '';
  
  const [scanning, setScanning] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [verifyMethod, setVerifyMethod] = useState('qr'); // 'qr' or 'otp'
  const [packageId, setPackageId] = useState(preselectedPackageId);
  const [otp, setOtp] = useState('');
  const [qrToken, setQrToken] = useState('');
  const [result, setResult] = useState(null);
  const [cameraError, setCameraError] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const startCamera = async () => {
    try {
      setCameraError(null);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setScanning(true);
      toast.info('Camera ready - scan RECIPIENT QR code');
    } catch (err) {
      setCameraError('Camera access denied. Please use OTP entry.');
      toast.error('Could not access camera');
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    setScanning(false);
  };

  const handleRelease = async () => {
    if (processing) return;
    if (!packageId.trim()) {
      toast.error('Please enter Package ID or Tracking Code');
      return;
    }
    
    if (verifyMethod === 'otp' && !otp.trim()) {
      toast.error('Please enter the OTP');
      return;
    }
    
    if (verifyMethod === 'qr' && !qrToken.trim()) {
      toast.error('Please scan the Recipient QR code');
      return;
    }
    
    setProcessing(true);
    stopCamera();
    
    try {
      // Use the /api/scan/recipient endpoint - ONLY way to mark as DELIVERED
      const payload = {
        package_id: packageId.trim(),
        gps_location: null
      };
      
      if (verifyMethod === 'otp') {
        payload.otp = otp.trim();
      } else {
        payload.recipient_qr_token = qrToken.trim();
      }
      
      const response = await api.post('/scan/recipient', payload);
      
      setResult({
        success: true,
        message: response.data.message || 'Package delivered successfully!',
        data: response.data
      });
      
      toast.success('Delivery complete!');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to complete delivery';
      setResult({
        success: false,
        message: errorMsg
      });
      toast.error(errorMsg);
    } finally {
      setProcessing(false);
    }
  };

  const handleQrScan = (scannedData) => {
    // When QR is scanned, extract the token
    setQrToken(scannedData);
    stopCamera();
    toast.success('QR code scanned!');
  };

  const resetScan = () => {
    setResult(null);
    setOtp('');
    setQrToken('');
    setPackageId('');
  };

  const sendOtp = async () => {
    if (!packageId.trim()) {
      toast.error('Enter Package ID first');
      return;
    }
    
    try {
      await api.post('/scan/recipient/generate-otp', {
        package_id: packageId.trim(),
        delivery_method: 'sms'
      });
      toast.success('OTP sent to recipient phone');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="pudo-release-page">
      {/* Header */}
      <header className="bg-green-600 text-white px-4 py-4">
        <div className="flex items-center gap-3 max-w-4xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate('/pudo')}
            className="p-2 hover:bg-green-500 rounded-full text-white"
            data-testid="back-button"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-2">
            <Users className="h-6 w-6" />
            <h1 className="text-lg font-bold">Release to Customer</h1>
          </div>
        </div>
      </header>

      <main className="max-w-md mx-auto p-4 space-y-6">
        {/* Instructions */}
        <div className="bg-green-50 border border-green-200 rounded-xl p-4">
          <h2 className="font-semibold text-green-800 mb-2">Complete Delivery</h2>
          <p className="text-sm text-green-700">
            Verify the recipient using their <strong>QR code</strong> or <strong>OTP</strong>.
            This is the only way to mark a package as delivered.
          </p>
        </div>

        {/* Result Display */}
        {result && (
          <div 
            className={`rounded-xl p-6 text-center ${
              result.success 
                ? 'bg-green-50 border border-green-200' 
                : 'bg-red-50 border border-red-200'
            }`}
            data-testid="scan-result"
          >
            {result.success ? (
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            ) : (
              <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            )}
            <h3 className={`text-xl font-bold mb-2 ${result.success ? 'text-green-800' : 'text-red-800'}`}>
              {result.success ? 'Delivery Complete!' : 'Release Failed'}
            </h3>
            <p className={`text-sm ${result.success ? 'text-green-700' : 'text-red-700'}`}>
              {result.message}
            </p>
            {result.success && (
              <div className="mt-4 bg-white rounded-lg p-4 text-left">
                <p className="text-sm text-gray-600">Status:</p>
                <p className="font-semibold text-green-600">✓ DELIVERED</p>
                {result.data?.tracking_code && (
                  <p className="text-sm text-gray-500 mt-1">
                    Tracking: {result.data.tracking_code}
                  </p>
                )}
              </div>
            )}
            <div className="mt-6 flex gap-3 justify-center">
              <Button
                onClick={resetScan}
                className="bg-green-600 hover:bg-green-700 text-white"
                data-testid="scan-another-btn"
              >
                Release Another
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/pudo')}
                data-testid="back-dashboard-btn"
              >
                Back to Dashboard
              </Button>
            </div>
          </div>
        )}

        {/* Release UI */}
        {!result && (
          <>
            {/* Package ID Input */}
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Package ID or Tracking Code
              </label>
              <Input
                type="text"
                value={packageId}
                onChange={(e) => setPackageId(e.target.value)}
                placeholder="Enter Package ID"
                className="w-full"
                data-testid="package-id-input"
              />
            </div>

            {/* Verification Method Toggle */}
            <div className="flex gap-2">
              <Button
                type="button"
                onClick={() => setVerifyMethod('qr')}
                className={`flex-1 py-3 rounded-xl flex items-center justify-center gap-2 ${
                  verifyMethod === 'qr' 
                    ? 'bg-green-600 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                data-testid="qr-method-btn"
              >
                <QrCode className="h-5 w-5" />
                Scan QR
              </Button>
              <Button
                type="button"
                onClick={() => setVerifyMethod('otp')}
                className={`flex-1 py-3 rounded-xl flex items-center justify-center gap-2 ${
                  verifyMethod === 'otp' 
                    ? 'bg-green-600 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                data-testid="otp-method-btn"
              >
                <KeyRound className="h-5 w-5" />
                Enter OTP
              </Button>
            </div>

            {/* QR Scan Method */}
            {verifyMethod === 'qr' && (
              <div className="space-y-4">
                {scanning ? (
                  <div className="relative rounded-xl overflow-hidden bg-black aspect-square">
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-48 h-48 border-2 border-green-400 rounded-2xl opacity-70" />
                    </div>
                    <Button
                      onClick={stopCamera}
                      className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-red-500 hover:bg-red-600"
                    >
                      Stop Camera
                    </Button>
                    <p className="absolute top-4 left-1/2 -translate-x-1/2 text-white text-sm bg-black/50 px-3 py-1 rounded-full">
                      Scan RECIPIENT QR (not Package QR)
                    </p>
                  </div>
                ) : (
                  <>
                    <Button
                      onClick={startCamera}
                      disabled={processing}
                      className="w-full py-8 bg-green-600 hover:bg-green-700 text-white rounded-xl flex flex-col items-center gap-2"
                      data-testid="start-camera-btn"
                    >
                      <Camera className="h-10 w-10" />
                      <span className="font-semibold">Scan Recipient QR</span>
                    </Button>
                    
                    {cameraError && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center text-sm text-amber-700">
                        {cameraError}
                      </div>
                    )}

                    {/* Manual QR Token Entry */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Or enter QR token manually
                      </label>
                      <Input
                        type="text"
                        value={qrToken}
                        onChange={(e) => setQrToken(e.target.value)}
                        placeholder="Recipient QR token"
                        className="w-full"
                        data-testid="qr-token-input"
                      />
                    </div>
                  </>
                )}
              </div>
            )}

            {/* OTP Method */}
            {verifyMethod === 'otp' && (
              <div className="space-y-4">
                <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Enter OTP from Customer
                  </label>
                  <Input
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    placeholder="Enter 6-digit OTP"
                    maxLength={6}
                    className="w-full text-center text-2xl tracking-widest font-mono"
                    data-testid="otp-input"
                  />
                </div>
                
                <Button
                  type="button"
                  variant="outline"
                  onClick={sendOtp}
                  className="w-full"
                  disabled={!packageId.trim()}
                  data-testid="send-otp-btn"
                >
                  <Smartphone className="h-4 w-4 mr-2" />
                  Send OTP to Recipient
                </Button>
              </div>
            )}

            {/* Submit Button */}
            <Button
              onClick={handleRelease}
              disabled={processing || !packageId.trim() || (verifyMethod === 'otp' ? !otp.trim() : !qrToken.trim())}
              className="w-full py-4 bg-green-600 hover:bg-green-700 text-white rounded-xl"
              data-testid="release-btn"
            >
              {processing ? (
                <span className="flex items-center gap-2">
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                  Processing...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5" />
                  Complete Delivery
                </span>
              )}
            </Button>

            {/* Warning */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center text-sm text-amber-700">
              <strong>Important:</strong> Package QR cannot be used here. 
              Only Recipient QR or OTP can complete delivery.
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default PudoRelease;
